import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useFileStore } from './FileProvider';
import { HtmlTooltip, StyledDarkRedInformationTypography, StyledInfoBox } from "../../../../components/commonComponents/StyledComponents/styled";
import { Close } from "@mui/icons-material";
import { loadLoopCampaignVisuals } from "../../../../api_calls/loopCampaign";
import { useSelector } from "react-redux";
import { checkUserDetailsFromCookie } from "../../../../api_calls/userManagement";
import TemporaryThumbnail from "./thumbnail/TemporaryThumbnail";
import ActiveInactiveVisualsList from "./ActiveInactiveVisualsList";

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16
};

const visualLimitForRetailer = (retailer, campaignTypeId) => {
    if (campaignTypeId === "unlimited") {
        return retailer?.unlimited_limit_count;
    }
    return retailer?.commercial_limit_count;
};

const videoExceedsSomeRetailerDuration = (durationSeconds, retailers) => {
    return retailers.some((retailer) => durationSeconds > Number(retailer?.max_duration) + 1);
};

// Tenant-root owner: clickable limits row + per-retailer table (Accordion like Listing Check).
const RetailerVisualLimitsPanel = ({
    visualUploadLimit,
    visualUploadDuration,
    retailers,
    campaignTypeId,
}) => {
    const [retailerDetailsExpanded, setRetailerDetailsExpanded] = React.useState(false);

    return (
        <Accordion
            expanded={retailerDetailsExpanded}
            onChange={() => setRetailerDetailsExpanded(!retailerDetailsExpanded)}
            sx={{
                mt: 1,
                mb: 1,
                // Expanded MUI margin (16px) and summary height would slide this row down.
                "&.MuiAccordion-root.Mui-expanded": {
                    marginTop: 1,
                    marginBottom: 1,
                },
                "&.MuiAccordion-root": {
                    boxShadow: "none",
                    "&:before": { display: "none" },
                },
            }}
        >
            <AccordionSummary
                expandIcon={null}
                sx={{
                    "&.MuiAccordionSummary-root": {
                        padding: 0,
                        minHeight: "auto",
                    },
                    "&.MuiAccordionSummary-root.Mui-expanded": {
                        minHeight: "auto",
                    },
                    "& .MuiAccordionSummary-content": {
                        margin: 0,
                        width: "100%",
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                        margin: 0,
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                    <Box sx={{ flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: "15px" }}>
                            <strong>Maximum number of visuals: {visualUploadLimit}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "15px" }}>
                            <strong>Maximum duration of videos: {visualUploadDuration} seconds</strong>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            flexGrow: 1,
                            borderBottom: "1px solid #AF3241",
                            minWidth: "24px",
                            alignSelf: "center",
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{ flexShrink: 0, color: "#AF3241", whiteSpace: "nowrap", fontSize: "15px" }}
                    >
                        <strong>{retailerDetailsExpanded === true ? "Hide retailer details" : "Show retailer details"}</strong>
                    </Typography>
                </Box>
            </AccordionSummary>
            {/* 16px plus the 2px title-cell padding matches the 18px gap above the drop zone. */}
            <AccordionDetails sx={{ "&.MuiAccordionDetails-root": { padding: 0, pt: 2 } }}>
                <Box
                    component="table"
                    sx={{
                        width: "auto",
                        borderCollapse: "collapse",
                        border: "none",
                        "& th, & td": {
                            border: "none",
                            fontSize: "14px",
                            padding: "2px 16px 2px 0",
                        },
                        "& th": {
                            fontWeight: 700,
                            color: "#666",
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ textAlign: "left" }}></th>
                            <th style={{ textAlign: "center" }}>Limit</th>
                            <th style={{ textAlign: "center" }}>Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {retailers.map((retailer) => (
                            <tr key={retailer.id_company ?? retailer.name}>
                                <td style={{ textAlign: "left", paddingLeft: "10px" }}>{retailer.name}</td>
                                <td style={{ textAlign: "center" }}>
                                    {visualLimitForRetailer(retailer, campaignTypeId)}
                                </td>
                                <td style={{ textAlign: "center" }}>{retailer.max_duration} seconds</td>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const Visuals = ({ usedFor, loopInfo }) => {
    const {
        acceptedFiles,
        duplicateFiles,
        rejectedFiles,
        longDurationRejectedFiles,
        maxFilesReached,
        resolutionRejectedFiles,
        invalidFiles,
        addFiles,
        removeFile,
        acceptedVideoDurations,
    } = useFileStore();

    const currentStateData = useSelector((state) => state.userSelections);
    const checkUserDetails = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    const [previews, setPreviews] = React.useState([]);
    const [fileDialogActive, setFileDialogActive] = React.useState(false);

    const retailerLimits = Array.isArray(currentStateData.loop_visual_limit_retailers)
        ? currentStateData.loop_visual_limit_retailers
        : [];
    const showRetailerLimits = retailerLimits.length > 0;
    const showRetailerDurationWarning =
        showRetailerLimits === true &&
        Object.values(acceptedVideoDurations || {}).some((durationSeconds) =>
            videoExceedsSomeRetailerDuration(durationSeconds, retailerLimits)
        );

    const maxFileSize = 50 * 1024 * 1024;
    const minFileInBytes = 6;

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'video/mp4': ['.mp4'],
        },
        maxSize: maxFileSize,
        minSize: minFileInBytes,
        noDragEventsBubbling: true,
        onFileDialogOpen: () => {
            setFileDialogActive(true);
        },
        onFileDialogCancel: () => {
            setFileDialogActive(false); // reset when dialog is closed
        },
        // Disable drag while file dialog is open
        noDrag: fileDialogActive,
        disabled: usedFor === 'update' && currentStateData.uploaded_loop_files_length >= currentStateData?.new_loop_data?.visual_upload_limit ? true : false,
        onDrop: (accepted, rejected) => {
            setFileDialogActive(false);
            addFiles(accepted, rejected);
        },
    });

    const convertToMb = (bytesValue) => {
        return (bytesValue / (1024 * 1024)).toFixed(2);
    };

    const thumbs = previews.map(({ file, url }) => {
        const isImage = file.type?.startsWith("image/");
        const isVideo = file.type?.startsWith("video/");

        return (
            <Box key={file.name} position="relative">
                <div style={{ position: "relative" }}>
                    {/* <Thumbnail url={url} type={isImage ? "image" : "video"} /> */}
                    <TemporaryThumbnail file={file} url={url} />
                    <HtmlTooltip title={"Remove Visual"}>
                        <IconButton
                            size="small"
                            onClick={() => removeFile(file.name)}
                            sx={{
                                position: "absolute",
                                top: -14,
                                right: -4,
                                background: "#E6E6E6",
                                color: "#AF3241",
                                '&:hover': {
                                    background: "#AF3241",
                                    color: "#E6E6E6"
                                },
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </HtmlTooltip>

                </div>
            </Box>

        );
    });

    React.useEffect(() => {
        const newPreviews = acceptedFiles.map(file => {
            return {
                file,
                url: URL.createObjectURL(file),
            };
        });

        setPreviews(newPreviews);

        // load visuals if usedFor is 'edit'
        if (usedFor === 'update') {
            loadLoopCampaignVisuals(loopInfo.id)
        }

        // Cleanup object URLs on unmount or files change
        return () => {
            newPreviews.forEach(p => URL.revokeObjectURL(p.url));
        };
    }, [acceptedFiles]);

    return (
        <>
            <Box height={10} />
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                Upload the visuals which you want to add to the loop-based campaign. Videos that are part of a loop are always played without sound. Please avoid a duplication of file names within the same campaign.
            </Typography>
            <Box height={10} />
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                <i>For an optimal playout, image files should fulfill the following specifications: Resolution = 1920x1080; File Size = max. 50 MB.</i>
            </Typography>
            {showRetailerLimits === true ? (
                <>
                    <RetailerVisualLimitsPanel
                        visualUploadLimit={currentStateData?.new_loop_data?.visual_upload_limit}
                        visualUploadDuration={currentStateData?.new_loop_data?.visual_upload_duration}
                        retailers={retailerLimits}
                        campaignTypeId={currentStateData?.new_loop_data?.loop_campaign_type_id}
                    />
                    {showRetailerDurationWarning === true && (
                        <Box width="100%" mt={1} mb={1}>
                            <StyledInfoBox>
                                <Typography variant="body2">
                                    A video file exceeds the maximum duration allowed by at least one retailer. Exclude stores of that retailer in the next step, unless exceeding a duration limit is intended.
                                </Typography>
                            </StyledInfoBox>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    <Typography variant="body2" sx={{ fontSize: "15px" }}>
                        <i>For video files, the maximum allowed duration is:  {currentStateData?.new_loop_data?.visual_upload_duration} seconds.</i>
                    </Typography>
                    <Box height={20} />
                    <Typography variant="body2" sx={{ fontSize: "15px" }}>
                        <strong> Maximum number of visuals: {currentStateData?.new_loop_data?.visual_upload_limit}</strong>
                    </Typography>
                </>
            )}
            <Box height={10} />

            {/* File Drop Area */}
            <Stack direction="row" spacing={2}>
                <Box
                    {...getRootProps({ className: "dropzone" })}
                    height={60}
                    width="100%"
                    sx={{
                        border: "2px dashed #888",
                        padding: "20px",
                        textAlign: "center",
                        borderRadius: "12px",
                        cursor: "pointer",
                    }}
                >
                    <input {...getInputProps()} />
                    {
                        usedFor === 'update' && currentStateData.uploaded_loop_files_length >= currentStateData?.new_loop_data?.visual_upload_limit ? (
                            <Box mt={2.5} sx={{ textAlign: "center" }}>
                                <Typography variant="body3" sx={{ margin: "20px" }}>
                                    Maximum file upload limit reached. You cannot upload more files.
                                </Typography>
                            </Box>
                        ) : <Box mt={1}>

                            <Typography variant="body3" sx={{ margin: "20px" }}>
                                Drag & Drop to Upload File(s) or Click to Browse
                            </Typography>
                            <Box height={4} />
                            <Typography variant="body2">
                                <em>.png, .jpg, .jpeg, or .mp4 (max. 1920x1080px)</em>
                            </Typography>
                        </Box>
                    }

                </Box>
            </Stack>

            {/* Accepted Files */}
            {acceptedFiles.length > 0 && (
                <Box mt={2}>
                    <h4>Accepted Files ({acceptedFiles.length})</h4>
                    <ul>
                        {acceptedFiles.map((file, index) => (
                            <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                                <Stack direction="row" spacing={2}>
                                    <Typography variant="body2">
                                        {file.name} - {convertToMb(file.size)} MB
                                    </Typography>
                                </Stack>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            {/* Rejected Files. The message stays on one line; the row has room to the right of the file name. */}
            {(rejectedFiles.length > 0 || duplicateFiles.length > 0 || longDurationRejectedFiles.length > 0 || maxFilesReached.length > 0 || invalidFiles.length !== 0 || resolutionRejectedFiles.length > 0) && (
                <Box mt={2}>
                    <h4>Rejected Files ({rejectedFiles.length + duplicateFiles.length + longDurationRejectedFiles.length + maxFilesReached.length + resolutionRejectedFiles.length + invalidFiles.length})</h4>
                    <ul>
                        {rejectedFiles.map((file, index) => (
                            <li key={`${file.name}-rejected-${index}`}>
                                <Stack direction="row" spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name} - {convertToMb(file.size)} MB
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            : {file.size > maxFileSize
                                                ? "File size exceeds the limit of 50 MB"
                                                : file.size < minFileInBytes
                                                    ? "File is empty."
                                                    : "Invalid file type. Allowed types are: .mp4, .jpg, .jpeg, or .png"}
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                        {/* ------ duplicate files ------- */}
                        {duplicateFiles.map((file, index) => (
                            <li key={`${file.name}-duplicate-${index}`}>
                                <Stack direction="row" spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name} - {convertToMb(file.size)} MB
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            : Rejected: Duplicate file. This file will not be uploaded.
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                        {/* --- rejected because of duration mismatch ----- */}
                        {
                            longDurationRejectedFiles.map((file, index) => (
                                <li key={`${file.name}-duplicate-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Rejected: Video duration too long. This file will not be uploaded.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))}
                        {/* ------- rejected because of resolution ----- */}
                        {
                            resolutionRejectedFiles.map((file, index) => (
                                <li key={`${file.name}-resolution-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Resolution too high. Resolution must not exceed 1920x1080px.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))}
                        {/* ------- rejected because of max upload limit ----- */}
                        {
                            maxFilesReached.map((file, index) => (
                                <li key={`${file.name}-max-files-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Rejected: Maximum number of visuals exceeded. This file will not be uploaded.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))
                        }
                        {/* -------- invalid file names ------------ */}
                        {invalidFiles.map((file, index) => (
                            <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                                <Stack direction={"row"} spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            :{" "}{"Invalid file name. The file name should only contain a-z, A-Z, 0-9, -, or _"}
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            <Box height={20} />
            {/* Thumbnails of accepted files */}
            <aside style={thumbsContainer}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {thumbs}
                </Box>

            </aside>

            <Box height={20} />
            {/* -------- active/inactive visuals list if usedFor===update */}
            <ActiveInactiveVisualsList loopInfo={loopInfo} usedFor={"listReview"} />
        </>
    );
};

export default Visuals;
